import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { Loader2, Plus } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  addTelnyxNumber,
  assignTelnyxNumbers,
  forgetTelnyxFaxApplication,
  getAgencies,
  getTelnyxFaxLine,
  listTelnyxFaxLines,
  provisionTelnyxFaxLine,
  setTelnyxOutboundNumber,
} from '../../services/api';
import type {
  TelnyxFaxLineResponse,
  TelnyxNumber,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import {
  extractDetailMessage,
  getBusinessErrorMessage,
  getConflictCode,
  getResponseDetail,
} from '../../utils/apiErrorDetail';
import { formatDateTimeInLocalZone } from '../../utils/dateUtils';
import { getHttpStatus } from '../../utils/httpErrorUtils';

const CONFIGURED_OFF =
  'Creating Telnyx fax applications is turned off on this server.';
const IN_PROGRESS =
  'A Telnyx fax application is being created for this agency. Try again in a minute.';
const NO_ANSWER =
  'Telnyx did not give a clear answer. Refresh in a minute and check again.';
const SENDING_FORGET =
  'This agency faxes through Telnyx now. Clear its outbound number first, which sends its order faxes through RingCentral again.';
/** Each forget refusal, keyed by the code Roger sends with its 409. */
const FORGET_REFUSALS: Record<string, string> = {
  sending: SENDING_FORGET,
  numbers_assigned:
    "This agency's numbers receive faxes on this application, and they would stop being accepted. Unlink its Telnyx numbers in the Fax numbers tab first.",
  create_in_flight:
    "A create of this agency's application is in progress. Try again in a few minutes.",
  create_unconfirmed:
    'The last create was never confirmed. Check the Telnyx portal, then resolve it with Create.',
};

const NOT_ASSIGNED =
  "Roger puts a number on the fax application once Telnyx holds it as active on Roger's account, so a port must complete first. If Telnyx did not answer, try again. A number on another Telnyx connection is never moved: move it in the Telnyx portal.";

/** A step that waits for a person to say yes, with what it costs in words. */
interface Confirmation {
  title: string;
  description: string;
  action: string;
  destructive: boolean;
  run: () => Promise<void>;
}

/** The read-only Sending line: Telnyx needs eFax on and a ready line. */
function describeSending(line: TelnyxFaxLineResponse): string {
  if (line.sending_via === 'telnyx') return 'Telnyx';
  if (!line.fax_product_on) return 'RingCentral (eFax product is off)';
  return `RingCentral (Telnyx line not ready: ${line.line_gap ?? 'unknown'})`;
}

/** A number as staff read it: the shown number, else the key it routes on. */
function describeNumber(number: TelnyxNumber): string {
  return number.fax_number ?? `${number.routing_key} (routing number)`;
}

/** The provision refusals each have their own fix, so each gets its own words. */
function provisionFailure(error: unknown): string {
  const status = getHttpStatus(error);
  if (status === 503) return CONFIGURED_OFF;
  if (status === 409) return IN_PROGRESS;
  // Roger's own 502: a duplicate name, or a create Telnyx did not confirm.
  if (status === 502) {
    return extractDetailMessage(getResponseDetail(error)) ?? NO_ANSWER;
  }
  return getBusinessErrorMessage(error) ?? 'Could not create the application.';
}

/**
 * Each agency's Telnyx fax line: its numbers, its fax application and the
 * number it sends from. An agency with eFax sends through Telnyx once its line
 * is ready, so completing the line here is what switches it.
 */
export const TelnyxFaxLinesPanel: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const [lines, setLines] = useState<TelnyxFaxLineResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [agencies, setAgencies] = useState<{ id: string; name: string }[]>([]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const response = await listTelnyxFaxLines({ throwOnError: true });
      setLines(response.data ?? []);
    } catch (error) {
      console.error('Failed to load Telnyx fax lines:', error);
      setLines([]);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // Every agency, because the picker is how an agency gets its first line.
    getAgencies({ query: { limit: 1000 }, throwOnError: true })
      .then(response =>
        setAgencies(
          (response.data ?? []).map(entry => ({
            id: entry.id,
            name: entry.system_name,
          }))
        )
      )
      .catch((error: unknown) => {
        console.error('Failed to load agencies:', error);
        showError('Could not load agencies', 'Reload the page to try again.');
      });
  }, [load, showError]);

  const replace = (updated: TelnyxFaxLineResponse) =>
    setLines(current =>
      current.some(line => line.agency_id === updated.agency_id)
        ? current.map(line =>
            line.agency_id === updated.agency_id ? updated : line
          )
        : [updated, ...current]
    );

  const openAgency = async (agencyId: string) => {
    if (!agencyId) return;
    try {
      const response = await getTelnyxFaxLine({
        path: { agency_id: agencyId },
        throwOnError: true,
      });
      replace(response.data);
    } catch (error) {
      console.error('Failed to load a Telnyx fax line:', error);
      showError('Could not open that agency', 'Try again.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <CardTitle>Telnyx Fax Lines</CardTitle>
            <CardDescription>
              Each agency&apos;s Telnyx numbers, fax application and outbound
              number. An agency with eFax sends through Telnyx once its line is
              ready.
            </CardDescription>
          </div>
          <div className='w-full sm:w-64'>
            <VirtualizedFormSelect
              label='Open an agency'
              placeholder='Choose an agency'
              options={agencies.map(agency => ({
                value: agency.id,
                label: agency.name,
              }))}
              sortOptions
              onChange={event => void openAgency(event.target.value)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin' />
          </div>
        ) : loadFailed ? (
          <div className='space-y-3 py-10 text-center'>
            <p className='text-destructive'>Could not load Telnyx fax lines.</p>
            <Button variant='outline' size='sm' onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : lines.length === 0 ? (
          <p className='py-10 text-center text-muted-foreground'>
            No agency has a Telnyx line yet. Choose an agency above.
          </p>
        ) : (
          <div className='divide-y divide-border'>
            {lines.map(line => (
              <TelnyxFaxLine
                key={line.agency_id}
                line={line}
                onChanged={replace}
                showError={showError}
                showSuccess={showSuccess}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface TelnyxFaxLineProps {
  line: TelnyxFaxLineResponse;
  onChanged: (line: TelnyxFaxLineResponse) => void;
  showError: (title: string, message?: string) => void;
  showSuccess: (title: string, message?: string) => void;
}

const TelnyxFaxLine: React.FC<TelnyxFaxLineProps> = ({
  line,
  onChanged,
  showError,
  showSuccess,
}) => {
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSettingOutbound, setIsSettingOutbound] = useState(false);
  const [confirming, setConfirming] = useState<Confirmation | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // Remounts the uncontrolled outbound select, so a cancelled change reverts.
  const [outboundReset, setOutboundReset] = useState(0);
  const agencyName = line.agency_name ?? line.agency_id;
  const sending = describeSending(line);
  const hasApplication = Boolean(line.telnyx_connection_id);
  const unassigned = hasApplication
    ? line.numbers.filter(entry => !entry.assigned)
    : [];

  const assign = async () => {
    setIsAssigning(true);
    try {
      const response = await assignTelnyxNumbers({
        path: { agency_id: line.agency_id },
        throwOnError: true,
      });
      onChanged(response.data);
      if (response.data.numbers.every(entry => entry.assigned)) {
        showSuccess('Numbers assigned', 'Every number is on the application.');
      } else {
        showError('Some numbers are not assigned yet', NOT_ASSIGNED);
      }
    } catch (error) {
      console.error('Failed to assign Telnyx numbers:', error);
      showError(
        'Could not assign the numbers',
        getHttpStatus(error) === 503
          ? 'Telnyx is turned off on this server.'
          : 'Reload and try again.'
      );
    } finally {
      setIsAssigning(false);
    }
  };

  const add = async () => {
    setIsAdding(true);
    try {
      const response = await addTelnyxNumber({
        path: { agency_id: line.agency_id },
        body: { number: number.trim(), label: label.trim() || null },
        throwOnError: true,
      });
      onChanged(response.data);
      setNumber('');
      setLabel('');
      showSuccess('Number added', 'Faxes to it reach this agency now.');
    } catch (error) {
      console.error('Failed to add a Telnyx number:', error);
      const status = getHttpStatus(error);
      showError(
        'Could not add that number',
        status === 409
          ? (extractDetailMessage(getResponseDetail(error)) ??
              'That number is already mapped to an agency.')
          : status === 422
            ? 'Enter a phone number, like (503) 555-0142.'
            : 'Reload and try again.'
      );
    } finally {
      setIsAdding(false);
    }
  };

  const create = async (confirmNotCreated = false) => {
    setIsCreating(true);
    try {
      const response = await provisionTelnyxFaxLine({
        path: { agency_id: line.agency_id },
        body: confirmNotCreated ? { confirm_not_created: true } : undefined,
        throwOnError: true,
      });
      onChanged(response.data);
      showSuccess(
        'Fax application ready',
        response.data.application_name ?? ''
      );
    } catch (error) {
      console.error('Failed to create a Telnyx fax application:', error);
      const detail = getResponseDetail(error);
      // An earlier create was never confirmed: only a person can clear it.
      if (
        getHttpStatus(error) === 409 &&
        getConflictCode(detail) === 'create_unconfirmed'
      ) {
        setConfirming({
          title: 'Check Telnyx before creating another',
          description:
            extractDetailMessage(detail) ??
            `The last create was never confirmed. Check the Telnyx portal for ${line.application_name ?? 'this agency'} before creating another.`,
          action: 'Telnyx has none, create it',
          destructive: false,
          run: () => create(true),
        });
      } else {
        showError('Could not create the application', provisionFailure(error));
      }
    } finally {
      setIsCreating(false);
    }
  };

  const forget = async () => {
    try {
      const response = await forgetTelnyxFaxApplication({
        path: { agency_id: line.agency_id },
        throwOnError: true,
      });
      onChanged(response.data);
      showSuccess(
        'Fax application forgotten',
        'Remove it in the Telnyx portal too.'
      );
    } catch (error) {
      console.error('Failed to forget a Telnyx fax application:', error);
      const detail = getResponseDetail(error);
      const code = getConflictCode(detail);
      showError(
        'Could not forget the application',
        (getHttpStatus(error) === 409 &&
          ((code && FORGET_REFUSALS[code]) ?? extractDetailMessage(detail))) ||
          'Reload and try again.'
      );
    }
  };

  const confirmForget = () =>
    setConfirming({
      title: 'Forget this fax application?',
      description: `Roger stops using ${line.application_name ?? 'this application'} for ${agencyName}, and stops accepting the inbound faxes sent to it. Nothing is deleted at Telnyx: remove the application in the Telnyx portal, or the next Create finds it and uses it again.`,
      action: 'Forget',
      destructive: true,
      run: forget,
    });

  const setOutbound = async (mappingId: string) => {
    setIsSettingOutbound(true);
    try {
      const response = await setTelnyxOutboundNumber({
        path: { agency_id: line.agency_id },
        body: { mapping_id: mappingId || null },
        throwOnError: true,
      });
      onChanged(response.data);
    } catch (error) {
      console.error('Failed to set the Telnyx outbound number:', error);
      showError(
        'Could not set the outbound number',
        getBusinessErrorMessage(error) ?? 'Reload and try again.'
      );
    } finally {
      setIsSettingOutbound(false);
    }
  };

  // With eFax on, the outbound number decides the provider: say what it costs.
  const chooseOutbound = (mappingId: string) => {
    const chosen = line.numbers.find(entry => entry.mapping_id === mappingId);
    const sendingNow = line.sending_via === 'telnyx';
    const startsSending =
      !sendingNow &&
      line.fax_product_on &&
      Boolean(line.telnyx_connection_id) &&
      chosen !== undefined;
    if (!sendingNow && !startsSending) {
      void setOutbound(mappingId);
      return;
    }
    setConfirming(
      chosen
        ? {
            title: sendingNow
              ? 'Change the outbound number?'
              : 'Start sending through Telnyx?',
            description: `${
              sendingNow
                ? `${agencyName} faxes through Telnyx now. Its order faxes go out from ${describeNumber(chosen)} at once`
                : `This agency's order faxes will start going out through Telnyx, from ${describeNumber(chosen)}, at once`
            }${chosen.assigned ? '' : ', and fail until Roger has put that number on its fax application'}.`,
            action: sendingNow ? 'Change it' : 'Start sending',
            destructive: true,
            run: () => setOutbound(mappingId),
          }
        : {
            title: 'Clear the outbound number?',
            description: `${agencyName} faxes through Telnyx now. Its order faxes go back to RingCentral until an outbound number is chosen again.`,
            action: 'Clear it',
            destructive: true,
            run: () => setOutbound(''),
          }
    );
  };

  const cancelConfirmation = () => {
    setConfirming(null);
    setOutboundReset(count => count + 1);
  };

  const runConfirmation = async () => {
    if (!confirming) return;
    setIsConfirming(true);
    try {
      await confirming.run();
    } finally {
      setIsConfirming(false);
      setConfirming(null);
      setOutboundReset(count => count + 1);
    }
  };

  return (
    <section
      aria-label={`Telnyx line for ${agencyName}`}
      className='space-y-4 py-5 first:pt-0 last:pb-0'
    >
      <div className='space-y-1'>
        <h3 className='font-medium'>{agencyName}</h3>
        <p>Sending: {sending}</p>
        {/* Shown with eFax off too, so the line can be finished first. */}
        {!line.fax_product_on && line.line_gap && (
          <p className='text-destructive'>Not ready: {line.line_gap}</p>
        )}
        <p className='text-sm text-muted-foreground'>
          eFax product: {line.fax_product_on ? 'on' : 'off'} in Roger Docs
          {line.products_synced_at
            ? `, last synced ${formatDateTimeInLocalZone(line.products_synced_at)}`
            : ', never synced, so it reads on'}
        </p>
      </div>

      <div className='space-y-2'>
        <h4 className='text-sm font-medium'>Numbers</h4>
        {line.numbers.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No Telnyx numbers yet.
          </p>
        ) : (
          <ul className='space-y-1 text-sm'>
            {line.numbers.map(entry => (
              <li key={entry.mapping_id}>
                {describeNumber(entry)}
                {entry.label ? ` · ${entry.label}` : ''}
                {hasApplication &&
                  (entry.assigned ? (
                    <span className='text-muted-foreground'>
                      {' '}
                      · On the application
                    </span>
                  ) : (
                    <span className='text-destructive'>
                      {' '}
                      · Not on the application yet
                    </span>
                  ))}
              </li>
            ))}
          </ul>
        )}
        {unassigned.length > 0 && (
          <div className='space-y-1'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => void assign()}
              disabled={isAssigning}
            >
              {isAssigning ? 'Assigning...' : 'Assign numbers'}
            </Button>
            <p className='text-sm text-muted-foreground'>{NOT_ASSIGNED}</p>
          </div>
        )}
        <div className='flex flex-col gap-2 md:flex-row'>
          <Input
            aria-label={`Telnyx number for ${agencyName}`}
            placeholder='(503) 555-0142'
            className='w-full md:w-48'
            value={number}
            onChange={event => setNumber(event.target.value)}
          />
          <Input
            aria-label={`Label for ${agencyName}`}
            placeholder='What it is for (optional)'
            className='w-full md:w-64'
            value={label}
            onChange={event => setLabel(event.target.value)}
          />
          <Button
            variant='outline'
            size='sm'
            onClick={() => void add()}
            disabled={!number.trim() || isAdding}
          >
            <Plus className='mr-1 h-4 w-4' />
            {isAdding ? 'Adding...' : 'Add Telnyx number'}
          </Button>
        </div>
      </div>

      <div className='space-y-2'>
        <h4 className='text-sm font-medium'>Fax application</h4>
        {line.telnyx_connection_id ? (
          <dl className='grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]'>
            <dt className='text-muted-foreground'>Name</dt>
            <dd className='break-all'>{line.application_name}</dd>
            <dt className='text-muted-foreground'>Connection id</dt>
            <dd className='break-all'>{line.telnyx_connection_id}</dd>
            <dt className='text-muted-foreground'>Created</dt>
            <dd>{formatDateTimeInLocalZone(line.provisioned_at)}</dd>
          </dl>
        ) : (
          <div className='space-y-2'>
            <p className='text-sm text-muted-foreground'>
              {line.provision_attempted_at
                ? `Last tried ${formatDateTimeInLocalZone(line.provision_attempted_at)}.`
                : 'Not created yet.'}
            </p>
            <Button
              size='sm'
              onClick={() => void create()}
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        )}
        {line.telnyx_connection_id && (
          <div className='space-y-1'>
            <Button
              variant='outline'
              size='sm'
              onClick={confirmForget}
              disabled={line.sending_via === 'telnyx'}
            >
              Forget application
            </Button>
            {line.sending_via === 'telnyx' && (
              <p className='text-sm text-muted-foreground'>{SENDING_FORGET}</p>
            )}
          </div>
        )}
      </div>

      <div className='space-y-2'>
        <div className='w-full md:w-72'>
          <VirtualizedFormSelect
            key={`outbound-${line.agency_id}-${line.outbound_mapping_id ?? 'none'}-${outboundReset}`}
            label='Outbound number'
            placeholder='None'
            options={line.numbers.map(entry => ({
              value: entry.mapping_id,
              label: describeNumber(entry),
            }))}
            defaultValue={line.outbound_mapping_id ?? ''}
            isClearable
            disabled={isSettingOutbound}
            onChange={event => chooseOutbound(event.target.value)}
          />
        </div>
      </div>

      <Dialog
        open={confirming !== null}
        onOpenChange={open => !open && !isConfirming && cancelConfirmation()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirming?.title}</DialogTitle>
            <DialogDescription>{confirming?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={cancelConfirmation}
              disabled={isConfirming}
            >
              Cancel
            </Button>
            <Button
              variant={confirming?.destructive ? 'destructive' : undefined}
              onClick={() => void runConfirmation()}
              disabled={isConfirming}
            >
              {confirming?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default TelnyxFaxLinesPanel;
